import type { FC, MouseEvent, TouchEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTime } from '@/hooks/useTime';
import { useViewportSize } from '@/hooks/useViewportSize';
import { useCenterPosition } from '@/hooks/useCenterPosition';
import { useBoundary } from '@/hooks/useBoundary';
import { getHandAngles } from '@/utils/timeAngles';
import { getRadiusAtAngle, getRayPathIntersection } from '@/utils/geometry';
import { getDemoShape, DEMO_SHAPE_TYPES } from '@/utils/demoShapes';
import type { DemoShapeType } from '@/utils/demoShapes';
import type { Point } from '@/types';
import { ClockHand } from './ClockHand';
import { CenterDot } from './CenterDot';
import { BoundaryCanvas } from './BoundaryCanvas';
import './Clock.css';

/** Gap between center dot and base of hands (in pixels) */
const CENTER_OFFSET = 12;

/** Margin from viewport/boundary edge (in pixels) */
const EDGE_MARGIN = 0;

/** Hand width configurations (in pixels) */
const HAND_WIDTH = {
  hour: 12,
  minute: 10,
  second: 6,
};

/** 
 * Hand length ratios
 * - minute/second: relative to distance to edge at current angle
 * - hour: relative to minute hand length (ensures hour < minute for readability)
 */
const HAND_RATIO = {
  hourOfMinute: 0.60, // Hour hand is 60% of minute hand length
  minute: 0.75,
  second: 0.95,
};

/**
 * Get the radius at a given angle, using boundary if available, otherwise viewport.
 * Also considers the drawing path for real-time feedback during drawing.
 */
function getEffectiveRadius(
  angle: number,
  boundary: { angleToRadius: Map<number, number> } | null,
  drawingPath: Point[],
  isDrawing: boolean,
  center: { x: number; y: number },
  viewport: { width: number; height: number }
): number {
  // Start with viewport radius as the base
  let radius = getRadiusAtAngle(center, viewport, angle);

  // If there's a completed boundary, use it
  if (boundary) {
    // Interpolate between precomputed angles for smooth transitions
    const lowerAngle = Math.floor(angle) % 360;
    const upperAngle = (lowerAngle + 1) % 360;
    const t = angle - Math.floor(angle);

    const r1 = boundary.angleToRadius.get(lowerAngle) ?? Infinity;
    const r2 = boundary.angleToRadius.get(upperAngle) ?? Infinity;

    const boundaryRadius = r1 + (r2 - r1) * t;
    radius = Math.min(radius, boundaryRadius);
  }

  // If currently drawing, also check the drawing path
  if (isDrawing && drawingPath.length >= 2) {
    const drawingRadius = getRayPathIntersection(center, angle, drawingPath);
    radius = Math.min(radius, drawingRadius);
  }

  return radius;
}

/**
 * Main Clock component.
 * Orchestrates time, positioning, boundary drawing, and renders clock hands.
 */
export const Clock: FC = () => {
  const time = useTime();
  const viewport = useViewportSize();
  const {
    center,
    isDragging: isCenterDragging,
    startDrag: startCenterDrag,
    updateDrag: updateCenterDrag,
    endDrag: endCenterDrag,
  } = useCenterPosition(viewport);
  const {
    boundary,
    drawingPoints,
    isDrawing,
    isRejected,
    startDrawing,
    continueDrawing,
    endDrawing,
    clearBoundary,
    recalculateBoundary,
    setDemoBoundary,
    setDemoDrawingPoints,
  } = useBoundary();

  // Track whether user has discovered features (for hiding hints)
  const [hasDrawnBoundary, setHasDrawnBoundary] = useState(false);
  const [hasDraggedCenter, setHasDraggedCenter] = useState(false);
  // Track dismissing animation state
  const [isDismissingBoundary, setIsDismissingBoundary] = useState(false);
  const [isDismissingDrag, setIsDismissingDrag] = useState(false);

  // Demo mode: track rapid clicks to trigger
  const clickCountRef = useRef(0);
  const lastClickTimeRef = useRef(0);
  const demoShapeIndexRef = useRef(0);
  const demoAnimationRef = useRef<number | null>(null);
  const [isDemoAnimating, setIsDemoAnimating] = useState(false);

  // Track previous center to detect changes
  const prevCenterRef = useRef(center);
  const initialCenterRef = useRef(center);

  // Recalculate boundary when center moves
  useEffect(() => {
    if (
      boundary &&
      (prevCenterRef.current.x !== center.x || prevCenterRef.current.y !== center.y)
    ) {
      recalculateBoundary(center);
    }
    prevCenterRef.current = center;
  }, [center, boundary, recalculateBoundary]);

  // Track when user has drawn a valid boundary
  useEffect(() => {
    if (boundary && !hasDrawnBoundary && !isDismissingBoundary) {
      setIsDismissingBoundary(true);
      // Remove hint after animation completes
      setTimeout(() => setHasDrawnBoundary(true), 800);
    }
  }, [boundary, hasDrawnBoundary, isDismissingBoundary]);

  // Track when user has dragged the center
  useEffect(() => {
    if (!hasDraggedCenter && !isDismissingDrag && !isCenterDragging) {
      const dx = center.x - initialCenterRef.current.x;
      const dy = center.y - initialCenterRef.current.y;
      const moved = Math.sqrt(dx * dx + dy * dy) > 20; // Moved more than 20px
      if (moved) {
        setIsDismissingDrag(true);
        // Remove hint after animation completes
        setTimeout(() => setHasDraggedCenter(true), 800);
      }
    }
  }, [center, isCenterDragging, hasDraggedCenter, isDismissingDrag]);

  // Helper to get point from touch event
  const getTouchPoint = useCallback((e: TouchEvent<HTMLDivElement>): Point | null => {
    const touch = e.touches[0] ?? e.changedTouches[0];
    if (!touch) return null;
    return { x: touch.clientX, y: touch.clientY };
  }, []);

  /**
   * Trigger demo mode: cycles through pre-defined shapes.
   * Animates the drawing of the shape progressively.
   * Activated by 5 rapid clicks (within 500ms between each).
   */
  const triggerDemoMode = useCallback(() => {
    // Cancel any existing animation
    if (demoAnimationRef.current !== null) {
      cancelAnimationFrame(demoAnimationRef.current);
      demoAnimationRef.current = null;
    }
    
    // Clear any existing boundary first
    clearBoundary();
    
    const shapeType = DEMO_SHAPE_TYPES[demoShapeIndexRef.current % DEMO_SHAPE_TYPES.length] as DemoShapeType;
    const demoPoints = getDemoShape(shapeType, center, viewport);
    
    // Animation settings
    const totalDuration = 1500; // Total animation duration in ms
    const startTime = performance.now();
    
    setIsDemoAnimating(true);
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / totalDuration, 1);
      
      // Calculate how many points to show based on progress
      // Use easeInOut for smoother animation
      const easedProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      const pointsToShow = Math.max(1, Math.floor(easedProgress * demoPoints.length));
      
      // Set all points up to current progress at once
      setDemoDrawingPoints(demoPoints.slice(0, pointsToShow));
      
      if (progress < 1) {
        // Continue animation
        demoAnimationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - finalize the boundary
        demoAnimationRef.current = null;
        setIsDemoAnimating(false);
        
        // Small delay before setting the final boundary for visual smoothness
        setTimeout(() => {
          setDemoBoundary(demoPoints, center);
        }, 50);
      }
    };
    
    // Start animation
    demoAnimationRef.current = requestAnimationFrame(animate);
    
    // Cycle to next shape for subsequent triggers
    demoShapeIndexRef.current = (demoShapeIndexRef.current + 1) % DEMO_SHAPE_TYPES.length;
  }, [center, viewport, setDemoBoundary, clearBoundary, setDemoDrawingPoints]);

  /**
   * Track clicks for demo mode activation.
   * Returns true if demo mode was triggered (to skip normal click handling).
   */
  const trackClickForDemo = useCallback((): boolean => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // Reset counter if more than 500ms since last click
    if (timeSinceLastClick > 500) {
      clickCountRef.current = 1;
    } else {
      clickCountRef.current += 1;
    }
    
    lastClickTimeRef.current = now;
    
    // Trigger demo mode on 5th rapid click
    if (clickCountRef.current >= 5) {
      clickCountRef.current = 0;
      triggerDemoMode();
      return true;
    }
    
    return false;
  }, [triggerDemoMode]);

  // Cleanup demo animation on unmount
  useEffect(() => {
    return () => {
      if (demoAnimationRef.current !== null) {
        cancelAnimationFrame(demoAnimationRef.current);
      }
    };
  }, []);

  // Mouse event handlers
  const handleMouseDown = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      // Check for demo mode trigger (5 rapid clicks)
      if (trackClickForDemo()) {
        return; // Demo mode activated, skip normal drawing
      }
      
      // Don't start new drawing during demo animation
      if (isDemoAnimating) return;
      
      if (isCenterDragging) return;
      const point = { x: e.clientX, y: e.clientY };
      startDrawing(point, center);
    },
    [isCenterDragging, startDrawing, center, trackClickForDemo, isDemoAnimating]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const point = { x: e.clientX, y: e.clientY };
      
      if (isCenterDragging) {
        updateCenterDrag(point, boundary?.points ?? null);
        return;
      }
      
      if (isDrawing) {
        continueDrawing(point, center);
      }
    },
    [isCenterDragging, updateCenterDrag, boundary, isDrawing, continueDrawing, center]
  );

  const handleMouseUp = useCallback(() => {
    if (isCenterDragging) {
      endCenterDrag();
      return;
    }
    endDrawing(center);
  }, [isCenterDragging, endCenterDrag, endDrawing, center]);

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      // Check for demo mode trigger (5 rapid taps)
      if (trackClickForDemo()) {
        return; // Demo mode activated, skip normal drawing
      }
      
      // Don't start new drawing during demo animation
      if (isDemoAnimating) return;
      
      if (isCenterDragging) return;
      const point = getTouchPoint(e);
      if (point) {
        startDrawing(point, center);
      }
    },
    [isCenterDragging, getTouchPoint, startDrawing, center, trackClickForDemo, isDemoAnimating]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      const point = getTouchPoint(e);
      if (!point) return;

      if (isCenterDragging) {
        e.preventDefault();
        updateCenterDrag(point, boundary?.points ?? null);
        return;
      }

      if (isDrawing) {
        e.preventDefault();
        continueDrawing(point, center);
      }
    },
    [isCenterDragging, getTouchPoint, updateCenterDrag, boundary, isDrawing, continueDrawing, center]
  );

  const handleTouchEnd = useCallback(() => {
    if (isCenterDragging) {
      endCenterDrag();
      return;
    }
    endDrawing(center);
  }, [isCenterDragging, endCenterDrag, endDrawing, center]);

  // Double-click/tap to clear boundary
  const handleDoubleClick = useCallback(() => {
    clearBoundary();
  }, [clearBoundary]);

  // Center dot drag start handler
  const handleCenterDragStart = useCallback(() => {
    startCenterDrag();
  }, [startCenterDrag]);

  // Calculate angles from current time
  const angles = getHandAngles(time);

  // Calculate distance to edge (boundary, drawing path, or viewport) at each hand's current angle
  const hourRadius = getEffectiveRadius(angles.hour, boundary, drawingPoints, isDrawing, center, viewport);
  const minuteRadius = getEffectiveRadius(angles.minute, boundary, drawingPoints, isDrawing, center, viewport);
  const secondRadius = getEffectiveRadius(angles.second, boundary, drawingPoints, isDrawing, center, viewport);

  // Calculate hand lengths
  // Minute and second are based on available radius at their angles
  const minuteLength = Math.max(0, (minuteRadius - CENTER_OFFSET - EDGE_MARGIN) * HAND_RATIO.minute);
  const secondLength = Math.max(0, (secondRadius - CENTER_OFFSET - EDGE_MARGIN) * HAND_RATIO.second);
  // Hour is a percentage of minute length, but clamped to available space at hour angle
  const hourMaxLength = Math.max(0, (hourRadius - CENTER_OFFSET - EDGE_MARGIN) * HAND_RATIO.minute);
  const hourLength = Math.min(minuteLength * HAND_RATIO.hourOfMinute, hourMaxLength);

  return (
    <div
      className="clock-container"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onDoubleClick={handleDoubleClick}
    >
      {/* Rejection flash overlay */}
      {isRejected && (
        <div className="rejection-flash" />
      )}

      {/* Boundary canvas layer */}
      <BoundaryCanvas
        width={viewport.width}
        height={viewport.height}
        boundaryPoints={boundary?.points ?? null}
        drawingPoints={drawingPoints}
        isDrawing={isDrawing}
      />

      {/* Clock hands */}
      <ClockHand
        center={center}
        angle={angles.hour}
        length={hourLength}
        width={HAND_WIDTH.hour}
        offset={CENTER_OFFSET}
      />

      <ClockHand
        center={center}
        angle={angles.minute}
        length={minuteLength}
        width={HAND_WIDTH.minute}
        offset={CENTER_OFFSET}
      />

      <ClockHand
        center={center}
        angle={angles.second}
        length={secondLength}
        width={HAND_WIDTH.second}
        offset={CENTER_OFFSET}
      />

      {/* Center dot (rendered last to be on top) */}
      <CenterDot
        center={center}
        isDragging={isCenterDragging}
        onDragStart={handleCenterDragStart}
      />

      {/* User guide hints */}
      {(!hasDrawnBoundary || !hasDraggedCenter) && (
        <div className="hints-container">
          {!hasDrawnBoundary && (
            <span className={`hint ${isDismissingBoundary ? 'hint-dismiss' : ''}`}>
              Draw a closed loop around the clock hands to create a custom clock face. Double click to clear the drawing.
            </span>
          )}
          {!hasDraggedCenter && (
            <span className={`hint ${isDismissingDrag ? 'hint-dismiss' : ''}`}>
              Drag the center dot to move the clock hands.
            </span>
          )}
        </div>
      )}
    </div>
  );
};
